import {useCallback, useEffect, useRef, useState} from 'react';
import {
    AvailableAnalysisClient,
    useFetchAvailableClientsQuery,
} from '@goproblems/shared/api/analysisClients';
import {AnalysisClientStatus} from '@types';
import {useMercure} from '@utils/hooks/useMercure';
import {useTranslation} from 'react-i18next';

type StatusUpdate = {
    clientId: string;
    name: string;
    isBusy: boolean;
    pendingRequestCount: number;
};

const getOverallStatus = (
    clients: AvailableAnalysisClient[],
    busyOverrides: Record<string, boolean>
): AnalysisClientStatus => {
    const hasOnline = clients.some(c => c.isOnline);
    if (!hasOnline) return AnalysisClientStatus.OFFLINE;
    const hasFree = clients.some(
        c => c.isOnline && !(busyOverrides[c.clientId] ?? c.isBusy)
    );
    return hasFree ? AnalysisClientStatus.READY : AnalysisClientStatus.BUSY;
};

const statusDotClasses: Record<AnalysisClientStatus, string> = {
    [AnalysisClientStatus.OFFLINE]: 'bg-neutral-400',
    [AnalysisClientStatus.BUSY]: 'bg-yellow-500',
    [AnalysisClientStatus.READY]: 'bg-green-500',
};

const statusGlowColors: Record<AnalysisClientStatus, string> = {
    [AnalysisClientStatus.OFFLINE]: '#a3a3a3',
    [AnalysisClientStatus.BUSY]: '#eab308',
    [AnalysisClientStatus.READY]: '#22c55e',
};

const statusLabelKeys: Record<AnalysisClientStatus, string> = {
    [AnalysisClientStatus.OFFLINE]: 'analysis_client.offline',
    [AnalysisClientStatus.BUSY]: 'analysis_client.busy',
    [AnalysisClientStatus.READY]: 'analysis_client.ready',
};

export const AnalysisClientIndicator = () => {
    const {t} = useTranslation();
    const {data} = useFetchAvailableClientsQuery();
    const [busyOverrides, setBusyOverrides] = useState<Record<string, boolean>>(
        {}
    );
    const [pendingOverride, setPendingOverride] = useState<number | null>(null);

    const handleStatusMessage = useCallback((raw: unknown) => {
        const message = raw as {type?: string; payload?: StatusUpdate};
        const update = message?.payload ?? (raw as StatusUpdate);
        if (update?.clientId) {
            setBusyOverrides(prev => ({
                ...prev,
                [update.clientId]: update.isBusy,
            }));
            setPendingOverride(update.pendingRequestCount);
        }
    }, []);

    const handleRequestCreated = useCallback((raw: unknown) => {
        const message = raw as {
            payload?: {pendingRequestCount?: number};
        };
        const pending = message?.payload?.pendingRequestCount;
        if (pending !== undefined) {
            setPendingOverride(pending);
        }
    }, []);

    useMercure({
        topic: '/analysis-clients/status',
        onMessage: handleStatusMessage,
    });

    useMercure({
        topic: '/invasions/analysis-requests',
        onMessage: handleRequestCreated,
    });

    if (!data) return null;

    const clients = data.clients;
    const pendingRequestCount = pendingOverride ?? data.pendingRequestCount;
    const status = getOverallStatus(clients, busyOverrides);
    const label = t(statusLabelKeys[status]);

    return (
        <IndicatorDot
            label={label}
            status={status}
            pendingRequestCount={pendingRequestCount}
        />
    );
};

const IndicatorDot = ({
    label,
    status,
    pendingRequestCount,
}: {
    label: string;
    status: AnalysisClientStatus;
    pendingRequestCount: number;
}) => {
    const {t} = useTranslation();
    const [isHovered, setIsHovered] = useState(false);
    const [tooltipAlign, setTooltipAlign] = useState<
        'center' | 'left' | 'right'
    >('center');
    const triggerRef = useRef<HTMLSpanElement | null>(null);
    const tooltipRef = useRef<HTMLSpanElement | null>(null);

    useEffect(() => {
        if (!isHovered) {
            setTooltipAlign('center');
            return;
        }

        const updateAlignment = () => {
            const trigger = triggerRef.current;
            const tooltip = tooltipRef.current;
            if (!trigger || !tooltip) return;

            const triggerRect = trigger.getBoundingClientRect();
            const tooltipWidth = tooltip.offsetWidth;
            const margin = 8;

            const centeredLeft =
                triggerRect.left + triggerRect.width / 2 - tooltipWidth / 2;
            const centeredRight = centeredLeft + tooltipWidth;

            if (centeredLeft < margin) {
                setTooltipAlign('left');
                return;
            }

            if (centeredRight > window.innerWidth - margin) {
                setTooltipAlign('right');
                return;
            }

            setTooltipAlign('center');
        };

        updateAlignment();
        window.addEventListener('resize', updateAlignment);

        return () => {
            window.removeEventListener('resize', updateAlignment);
        };
    }, [isHovered]);

    const tooltipPositionClass =
        tooltipAlign === 'left'
            ? 'left-0'
            : tooltipAlign === 'right'
              ? 'right-0'
              : 'left-1/2 -translate-x-1/2';

    const arrowPositionClass =
        tooltipAlign === 'left'
            ? 'left-2'
            : tooltipAlign === 'right'
              ? 'right-2'
              : 'left-1/2 -translate-x-1/2';

    return (
        <span
            ref={triggerRef}
            className="relative flex items-center cursor-default"
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}>
            <span
                className={`block w-2.5 h-2.5 rounded-full ${statusDotClasses[status]}`}
                style={{boxShadow: `0 0 4px ${statusGlowColors[status]}`}}
            />

            {isHovered && (
                <span
                    ref={tooltipRef}
                    className={`absolute bottom-full mb-1.5 px-2.5 py-1.5 rounded-md bg-black/85 text-white text-xs whitespace-nowrap z-[1000] pointer-events-none leading-snug ${tooltipPositionClass}`}>
                    <div className="font-semibold">
                        {t('analysis_client.title')}: {label}
                    </div>
                    <div className="opacity-85">
                        {t('analysis_client.pending', {
                            count: pendingRequestCount,
                        })}
                    </div>
                    {status === AnalysisClientStatus.OFFLINE && (
                        <div className="opacity-85">
                            {t('analysis_client.no_clients')}
                        </div>
                    )}
                    <span
                        className={`absolute top-full border-x-[5px] border-x-transparent border-t-[5px] border-t-black/85 ${arrowPositionClass}`}
                    />
                </span>
            )}
        </span>
    );
};
